import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, TextField, Button } from "@mui/material";
import apiService from "../../service";
import ToastMessage from "../Common/ReactToastifyNotification/Toastify";
import { useNavigate } from "react-router-dom";
import SignupBg from "../../assets/Login-img.png";
import MainLogo from "../../assets/Hawkai-logo.png";
import VerificationIcon from "../../assets/Verification.png";
import "./Signup.css";
import DrawerLogo from "../../assets/DrawerLogo.png";

const VerificationValidation = () => {
  const navigate = useNavigate();
  const emaildetails =
    localStorage.getItem("email") || "your-email@example.com";
  const [otpDigits, setOtpDigits] = useState(Array(6).fill(""));
  const [otpError, setOtpError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const inputsRef = useRef([]);

  useEffect(() => {
    // focus first input on mount
    if (inputsRef.current && inputsRef.current[0]) inputsRef.current[0].focus();
  }, []);

  const handleVerifyOtp = async (codeArg) => {
    setOtpError("");
    const code = (codeArg || otpDigits.join("")).trim();
    if (!/^[0-9]{6}$/.test(code)) {
      setOtpError("Please enter a valid 6-digit OTP");
      return;
    }
    setVerifying(true);
    try {
      const verifyResponse = await apiService.verificationValid({ otp: code });
      if (verifyResponse?.status === "true") {
        ToastMessage.success(verifyResponse.message);

        const accessList = verifyResponse?.data?.access_list || [];
        const consumeType = verifyResponse?.data?.consumer_type || "";
        localStorage.setItem("userType", consumeType);

        if (!Array.isArray(accessList)) {
          console.error("Invalid accessList format", accessList);
          navigate("/login");
          return;
        }
        localStorage.setItem("access", JSON.stringify(accessList));
        if (accessList.includes("IDCriminalDocument")) {
          navigate("/my-ai/criminal-document");
        } else {
          navigate("/doc-ai/invoice");
        }
      } else {
        const msg = verifyResponse?.message || "OTP verification failed";
        setOtpError(msg);
        ToastMessage.error(msg);
      }
    } catch (error) {
      const msg =
        (error && (error.message || error?.data?.message)) ||
        "OTP verification failed";
      ToastMessage.error(msg);
    } finally {
      setVerifying(false);
    }
  };

  const handleDigitChange = (e, idx) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 1);
    setOtpDigits((prev) => {
      const next = [...prev];
      next[idx] = val;
      return next;
    });
    if (val) {
      // move focus to next
      const nextEl = inputsRef.current[idx + 1];
      if (nextEl) nextEl.focus();
    }
    // auto-submit if all digits entered
    setTimeout(() => {
      const code = (inputsRef.current || [])
        .map((_, i) => otpDigits[i] || (i === idx ? val : ""))
        .join("");
      if (/^[0-9]{6}$/.test(code)) {
        handleVerifyOtp(code);
      }
    }, 50);
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      if (otpDigits[idx]) {
        // clear current
        setOtpDigits((prev) => {
          const next = [...prev];
          next[idx] = "";
          return next;
        });
      } else {
        const prevEl = inputsRef.current[idx - 1];
        if (prevEl) {
          prevEl.focus();
          setOtpDigits((prev) => {
            const next = [...prev];
            next[idx - 1] = "";
            return next;
          });
        }
      }
    } else if (e.key === "ArrowLeft") {
      const prevEl = inputsRef.current[idx - 1];
      if (prevEl) prevEl.focus();
    } else if (e.key === "ArrowRight") {
      const nextEl = inputsRef.current[idx + 1];
      if (nextEl) nextEl.focus();
    }
  };

  const handlePaste = (e, idx) => {
    e.preventDefault();
    const paste = (e.clipboardData || window.clipboardData).getData("text");
    const digits = paste
      .replace(/\D/g, "")
      .slice(0, 6 - idx)
      .split("");
    if (digits.length === 0) return;
    setOtpDigits((prev) => {
      const next = [...prev];
      for (let i = 0; i < digits.length; i++) {
        next[idx + i] = digits[i];
        if (inputsRef.current[idx + i])
          inputsRef.current[idx + i].value = digits[i];
      }
      return next;
    });
    // focus the last pasted
    const focusIndex = Math.min(5, idx + digits.length);
    const focusEl = inputsRef.current[focusIndex];
    if (focusEl) focusEl.focus();
    setTimeout(() => {
      const code = (otpDigits.map((d, i) => otpDigits[i]) || []).join("");
      if (/^[0-9]{6}$/.test(code)) {
        handleVerifyOtp(code);
      }
    }, 50);
  };

  return (
    <Box className="mains-container">
      {/* Left Image Section */}
      <Box className="image-container">
        {/* <img src={SignupBg} alt="Background" className="background-image" />
         */}
        <div className="bg-logo"></div>
      </Box>

      {/* Right Verification Section */}
      <Box className="right-container">
        <Box className="verification-box">
          <div>
            <img src={DrawerLogo} alt="Logo" className="Dlogo" />
          </div>
          <div>
            <img
              src={VerificationIcon}
              alt="Verification Email"
              className="verification-icon"
            />
          </div>

          <Typography className="verification-title">
            Confirm your email
          </Typography>
          <Typography className="verification-text">
            We’ve sent an email to <span>'{emaildetails}'</span> Check your
            inbox to activate your account.
          </Typography>

          <Typography className="verification-subText">
            You can complete your registration by clicking the link in the email
            or by entering the OTP from the email below.
          </Typography>

          <div className="otp-container">
            <div className="otp-input-row">
              {otpDigits.map((digit, idx) => (
                <TextField
                  key={idx}
                  variant="outlined"
                  value={digit}
                  inputRef={(el) => (inputsRef.current[idx] = el)}
                  onChange={(e) => handleDigitChange(e, idx)}
                  onKeyDown={(e) => handleKeyDown(e, idx)}
                  onPaste={(e) => handlePaste(e, idx)}
                  className="otp-digit"
                  inputProps={{
                    maxLength: 1,
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    style: { textAlign: "center" },
                  }}
                  error={!!otpError}
                />
              ))}
            </div>
            <Button
              variant="contained"
              style={{
                backgroundColor: "#000000",
                color: "#FFFFFF",
                textTransform: "none",
                boxShadow: "none",
                borderRadius: "8px",
                height: "48px",
                fontSize: "16px",
                fontWeight: 600,
                fontFamily: "Public Sans",
                marginTop: "30px",
              }}
              className="otp-verify-button credit-button"
              onClick={() => handleVerifyOtp()}
              disabled={verifying || otpDigits.some((d) => !d)}
            >
              {verifying ? "Verifying..." : "Verify"}
            </Button>
          </div>

          <Typography className="resend-text">
            {/* Didn’t receive an email? <span className="resend-link">Send again</span> */}
          </Typography>

          {/* <Typography className="signup-text">
            Take me back to <span className="signup-link" onClick={()=>navigate('/signup')}>Sign Up</span>
          </Typography> */}
        </Box>
      </Box>
    </Box>
  );
};

export default VerificationValidation;
